import { Component, OnInit, OnDestroy, signal, inject, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../core/service/api-service/api-service';
import { API } from '../../../core/API';

import { isAllowedImage, readImagePreview, IMAGE_FORMAT_ERROR } from '../../../shared/utils/image-upload';
import { SwalUtils } from '../../../shared/utils/swal.utils';
@Component({
  selector: 'app-int-instances-list',
  standalone: false,
  templateUrl: './int-instances-list.component.html',
  styleUrls: ['./int-instances-list.component.css']
})
export class IntInstancesListComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private destroy$ = new Subject<void>();

  instances     = signal<any[]>([]);
  isLoading     = signal(true);
  errorMessage  = signal<string | null>(null);
  selectedStatus = signal('');

  filteredInstances = computed(() => {
    const s = this.selectedStatus();
    if (!s) return this.instances();
    return this.instances().filter(i => (i.status || 'ACTI') === s);
  });

  // Add / edit form
  showForm          = signal(false);
  editingInstance   = signal<any>(null);
  isSaving          = signal(false);
  formData          = signal({ name: '', acronym: '', level: '', website: '', member_since: '', description: '', pays_membres: '', fondee: '', siege: '' });
  logoFile          = signal<File | null>(null);
  logoPreview       = signal<string | null>(null);

  // Status confirmation
  pendingStatus = signal<{ id: number; status: 'ACTI' | 'INAC' } | null>(null);

  // Archive confirmation
  pendingArchive = signal<number | null>(null);

  ngOnInit(): void { this.loadInstances(); }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  onStatusFilter(value: string): void { this.selectedStatus.set(value); }

  loadInstances(): void {
    this.isLoading.set(true);
    this.api.get(API.INT_INSTANCES.GET_ALL)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r: any) => { this.instances.set(r.data || []); this.isLoading.set(false); },
        error: () => { this.errorMessage.set('Erreur lors du chargement'); this.isLoading.set(false); }
      });
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  openAddForm(): void {
    this.editingInstance.set(null);
    this.formData.set({ name: '', acronym: '', level: '', website: '', member_since: '', description: '', pays_membres: '', fondee: '', siege: '' });
    this.logoFile.set(null); this.logoPreview.set(null); this.errorMessage.set(null);
    this.showForm.set(true);
  }

  openEditForm(instance: any): void {
    this.editingInstance.set(instance);
    this.formData.set({ name: instance.name_ln1 || '', acronym: instance.acronym || '', level: instance.level || '', website: instance.website || '', member_since: instance.member_since || '', description: instance.description || '', pays_membres: instance.pays_membres || '', fondee: instance.fondee || '', siege: instance.siege || '' });
    this.logoFile.set(null); this.logoPreview.set(instance.logo_url || null); this.errorMessage.set(null);
    this.showForm.set(true);
  }

  closeForm(): void { this.showForm.set(false); this.editingInstance.set(null); this.logoFile.set(null); this.logoPreview.set(null); this.errorMessage.set(null); }

  onLogoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) return;
    if (!isAllowedImage(file)) { SwalUtils.error('Format invalide', IMAGE_FORMAT_ERROR); return; }
    this.logoFile.set(file);
    readImagePreview(file, (url) => this.logoPreview.set(url));
  }

  updateField(field: string, value: any): void { this.formData.set({ ...this.formData(), [field]: value }); }

  onSave(): void {
    if (!this.formData().name.trim()) { this.errorMessage.set('Le nom est requis'); return; }
    if (!this.formData().acronym.trim()) { this.errorMessage.set('L\'acronyme est requis'); return; }
    if (!this.formData().description.trim()) { this.errorMessage.set('La description est requise'); return; }
    this.isSaving.set(true); this.errorMessage.set(null);
    const form = new FormData();
    form.append('name_ln1', this.formData().name);
    form.append('acronym', this.formData().acronym);
    form.append('level', this.formData().level);
    form.append('website', this.formData().website);
    form.append('member_since', this.formData().member_since);
    if (this.formData().description) form.append('description', this.formData().description);
    if (this.formData().pays_membres) form.append('pays_membres', this.formData().pays_membres);
    if (this.formData().fondee) form.append('fondee', this.formData().fondee);
    if (this.formData().siege) form.append('siege', this.formData().siege);
    const logo = this.logoFile();
    if (logo) form.append('logo', logo);
    const editing = this.editingInstance();
    const req$ = editing
      ? this.api.put(`${API.INT_INSTANCES.UPDATE}/${editing.id_instance}`, form)
      : this.api.post(API.INT_INSTANCES.ADD, form);
    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.isSaving.set(false); this.closeForm(); this.loadInstances(); },
      error: () => { this.errorMessage.set('Erreur lors de l\'enregistrement'); this.isSaving.set(false); }
    });
  }

  // ── Status ────────────────────────────────────────────────────────────────
  confirmStatus(instance: any, status: 'ACTI' | 'INAC', $event: MouseEvent): void {
    $event.stopPropagation();
    this.pendingStatus.set({ id: instance.id_instance, status });
  }
  cancelStatus(): void { this.pendingStatus.set(null); }
  applyStatus(): void {
    const p = this.pendingStatus();
    if (!p) return;
    this.pendingStatus.set(null);
    this.api.patch(`${API.INT_INSTANCES.SET_STATUS}/${p.id}`, { status: p.status })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.instances.update(list => list.map(i => i.id_instance === p.id ? { ...i, status: p.status } : i)),
        error: () => this.errorMessage.set('Erreur lors du changement de statut')
      });
  }

  // ── Archive ───────────────────────────────────────────────────────────────
  confirmArchive(id: number, $event: MouseEvent): void {
    $event.stopPropagation();
    this.pendingArchive.set(id);
  }
  cancelArchive(): void { this.pendingArchive.set(null); }
  applyArchive(): void {
    const id = this.pendingArchive();
    if (!id) return;
    this.pendingArchive.set(null);
    this.api.delete(`${API.INT_INSTANCES.DELETE}/${id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadInstances(),
        error: () => this.errorMessage.set('Erreur lors de l\'archivage')
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  statusLabel(status: string): string { return status === 'ACTI' ? 'En ligne' : 'Hors ligne'; }
  statusBadgeClass(status: string): string {
    return status === 'ACTI'
      ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
      : 'bg-amber-50 text-amber-600 border-amber-100';
  }
}
